/* monowind companion stylesheet — loaded into the light DOM by the app.
 * The shadow root has its own baked-in styles; this file targets the light-DOM
 * subtree the host owns.
 *
 * Layering, on Tailwind's layer names: defaults and normalization that
 * USERS may override live in `@layer base` — Tailwind's utilities layer
 * (and any unlayered author CSS) then wins over them, matching Tailwind's
 * own preflight behavior. The locks the ENGINE owns (geometry, typography,
 * the paint the grid takes over) live in `@layer theme`, the first layer,
 * with `!important`: among important declarations the earliest layer
 * wins, so they beat every later layer's, a utility's `!` modifier
 * included, and any unlayered author `!important`; only an inline
 * `style` marked important (element-attached) and a running transition
 * still beat them. The plain engine rules stay unlayered, ahead of every
 * layer's plain declarations.
 *
 * A native region's contents (`data-mw-native`, specs/native-regions.md
 * "The locks") are the browser's. A rule they could match comes as twin
 * selectors: one for a host holding no region (`data-mw-no-regions`),
 * one for a host holding some (`data-mw-regions`) whose subject skips,
 * in a `:where()`, the contents — and the region itself, for a lock on
 * what a box holds. The host's mark is a plain attribute in the lock
 * layer, which the style engines reject fast
 * (architecture/performance.md), a `:where()` elsewhere, where authors'
 * rules meet ours on specificity. A rule whose subject is an engine
 * flag takes one selector. A subject in `:is()` is filed under none of
 * Chromium's buckets, tried on every element: a list of selectors takes
 * its place, each filed under its own tag or attribute. */

/* Gap-decoration utilities + engine mirrors (specs/gap-decorations.md)
 * and the synthesized pointer-state variants — Tailwind source, split
 * out so cdn.ts can hand them to the in-browser compiler (this file is
 * plain CSS there). Imports must precede all rules, per @import
 * syntax. */
@import "./utilities.css";
@import "./variants.css";

/* Tailwind's order for the two layers this file uses, so a page that
 * loads the companion before Tailwind orders them the same way. */
@layer theme, base;

@layer base {
  mono-wind {
    display: block;
    position: relative;
    /* New stacking context so ancestor z-index / blend-modes don't bleed in
     * and our own absolute-positioned decorations don't leak out. */
    isolation: isolate;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 14px;
    /* `normal` picks a line box tall enough for the font's ascent +
     * descent so inline span backgrounds fit within a row instead of
     * bleeding into the next. Cell height derives from the probe, so
     * this stays deterministic — just taller. */
    line-height: normal;
    letter-spacing: 0;
    /* The grid needs every glyph at one advance: synthetic bold widens
     * them in Safari (faces with a real bold are unaffected); synthetic
     * oblique only slants, so style synthesis stays allowed. */
    font-synthesis: style;
    /* `--mw-fg`/`--mw-bg` are the engine's, derived from the host's
     * colors into its shadow's `:host` rule (specs/theming.md). */
    /* ANSI-16 theme tokens (specs/theming.md): what SGR-colored ascii
     * art and effect filters resolve against, so a "red" follows the
     * theme. Defaults follow the VS Code terminal palette. */
    --mw-ansi-black: #000000;
    --mw-ansi-red: #cd3131;
    --mw-ansi-green: #0dbc79;
    --mw-ansi-yellow: #e5e510;
    --mw-ansi-blue: #2472c8;
    --mw-ansi-magenta: #bc3fbc;
    --mw-ansi-cyan: #11a8cd;
    --mw-ansi-white: #e5e5e5;
    --mw-ansi-bright-black: #666666;
    --mw-ansi-bright-red: #f14c4c;
    --mw-ansi-bright-green: #23d18b;
    --mw-ansi-bright-yellow: #f5f543;
    --mw-ansi-bright-blue: #3b8eea;
    --mw-ansi-bright-magenta: #d670d6;
    --mw-ansi-bright-cyan: #29b8db;
    --mw-ansi-bright-white: #ffffff;
  }

  /* Normalize native form controls so they don't borrow browser chrome that
   * would break the grid. Padding, margin, border-width, and box-sizing are
   * handled by the engine-owned rules below. In `base` so utilities
   * (text-*, bg-*, …) can restyle controls. */
  mono-wind:where([data-mw-no-regions]) button,
  mono-wind:where([data-mw-no-regions]) input,
  mono-wind:where([data-mw-no-regions]) select,
  mono-wind:where([data-mw-no-regions]) textarea,
  mono-wind:where([data-mw-regions]) button:where(:not([data-mw-native], [data-mw-native] *)),
  mono-wind:where([data-mw-regions]) input:where(:not([data-mw-native], [data-mw-native] *)),
  mono-wind:where([data-mw-regions]) select:where(:not([data-mw-native], [data-mw-native] *)),
  mono-wind:where([data-mw-regions]) textarea:where(:not([data-mw-native], [data-mw-native] *)) {
    border-radius: 0;
    color: inherit;
    background: transparent;
    font: inherit;
    line-height: inherit;
    text-align: inherit;
  }
  /* A checkbox's or a radio's appearance is the author's to read: the
   * grid draws the widget's glyphs (specs/checkboxes.md). */
  mono-wind:where([data-mw-no-regions]) button,
  mono-wind:where([data-mw-no-regions]) input:not([type="checkbox"], [type="radio"]),
  mono-wind:where([data-mw-no-regions]) select,
  mono-wind:where([data-mw-no-regions]) textarea,
  mono-wind:where([data-mw-regions]) button:where(:not([data-mw-native], [data-mw-native] *)),
  mono-wind:where([data-mw-regions])
    input:not([type="checkbox"], [type="radio"]):where(:not([data-mw-native], [data-mw-native] *)),
  mono-wind:where([data-mw-regions]) select:where(:not([data-mw-native], [data-mw-native] *)),
  mono-wind:where([data-mw-regions]) textarea:where(:not([data-mw-native], [data-mw-native] *)) {
    appearance: none;
  }

  /* Engine-drawn scrollbar ink defaults to currentColor, thumb and
   * track alike — like borders (specs/scrolling.md); `auto` paints
   * uncolored. Tailwind's scrollbar-thumb-* / scrollbar-track-*
   * utilities compose through its --tw-scrollbar-* variables
   * (registered with a transparent initial), so seeding those keeps
   * the other half visible when only one is set. The property itself
   * stays untouched: it inherits into native scrollers (textareas,
   * shadow trees), where equal thumb and track colors would hide the
   * thumb. */
  mono-wind:where([data-mw-no-regions]) *,
  mono-wind:where([data-mw-regions]) *:where(:not([data-mw-native], [data-mw-native] *)) {
    --tw-scrollbar-thumb: currentColor;
    --tw-scrollbar-track: currentColor;
  }

  /* Form controls render their value/caret/selection natively — the
   * fill lock below excludes them via `:not()`, so this visible color
   * and themed caret actually stick. */
  mono-wind:where([data-mw-no-regions]) input,
  mono-wind:where([data-mw-no-regions]) textarea,
  mono-wind:where([data-mw-no-regions]) select,
  mono-wind:where([data-mw-regions]) input:where(:not([data-mw-native], [data-mw-native] *)),
  mono-wind:where([data-mw-regions]) textarea:where(:not([data-mw-native], [data-mw-native] *)),
  mono-wind:where([data-mw-regions]) select:where(:not([data-mw-native], [data-mw-native] *)) {
    color: var(--mw-fg);
    caret-color: var(--mw-fg);
  }

  /* Placeholder at half the element's own color, so it tracks both the
   * theme and the focus-invert. Explicit opacity: Firefox dims its UA
   * placeholder with opacity, which would double with the color mix. */
  mono-wind:where([data-mw-no-regions]) input::placeholder,
  mono-wind:where([data-mw-no-regions]) textarea::placeholder,
  mono-wind:where([data-mw-regions])
    input:where(:not([data-mw-native], [data-mw-native] *))::placeholder,
  mono-wind:where([data-mw-regions])
    textarea:where(:not([data-mw-native], [data-mw-native] *))::placeholder {
    color: color-mix(in srgb, currentcolor 50%, transparent);
    opacity: 1;
  }

  /* A required <select> still on its empty placeholder option matches
   * :invalid — same placeholder treatment (the :invalid specificity
   * beats the form-control color above; the pair below beats the
   * focus-invert). currentcolor would self-refer here, so the themed
   * colors are spelled out. */
  mono-wind:where([data-mw-no-regions]) select:invalid,
  mono-wind:where([data-mw-regions])
    select:invalid:where(:not([data-mw-native], [data-mw-native] *)) {
    color: color-mix(in srgb, var(--mw-fg) 50%, transparent);
  }
  mono-wind:where([data-mw-no-regions]) select:invalid:focus-visible,
  mono-wind:where([data-mw-regions])
    select:invalid:focus-visible:where(:not([data-mw-native], [data-mw-native] *)) {
    color: color-mix(in srgb, var(--mw-bg) 50%, transparent);
  }

  /* A disabled checkbox or radio at half strength, as a placeholder;
   * its state, whose transition tells the host of a flip
   * (specs/checkboxes.md). */
  mono-wind:where([data-mw-no-regions]) input:is([type="checkbox"], [type="radio"]):disabled,
  mono-wind:where([data-mw-regions])
    input:is([type="checkbox"], [type="radio"]):disabled:where(
      :not([data-mw-native], [data-mw-native] *)
    ) {
    color: color-mix(in srgb, var(--mw-fg) 50%, transparent);
  }
  mono-wind:where([data-mw-no-regions]) input:is([type="checkbox"], [type="radio"]):checked,
  mono-wind:where([data-mw-regions])
    input:is([type="checkbox"], [type="radio"]):checked:where(
      :not([data-mw-native], [data-mw-native] *)
    ) {
    --mw-checked: 1;
  }
  mono-wind:where([data-mw-no-regions]) input:is([type="checkbox"], [type="radio"]):indeterminate,
  mono-wind:where([data-mw-regions])
    input:is([type="checkbox"], [type="radio"]):indeterminate:where(
      :not([data-mw-native], [data-mw-native] *)
    ) {
    --mw-checked: 2;
  }
  /* While the engine reads, a checkbox or radio is its last layout's
   * cells wide, an intrinsic width, which the engine's read of the
   * author's leaves `auto` (specs/checkboxes.md "Sizing"). */
  mono-wind input:is([type="checkbox"], [type="radio"])[data-mw-measuring] {
    contain: inline-size;
    contain-intrinsic-inline-size: calc(var(--mw-tc, 3) * var(--mw-cw, 1ch));
  }

  /* Single-line controls: never wrap, clip overflow. The one-row
   * floor lives in the tree builder (a CSS min-height on the outer
   * box gives no content-area guarantee once border + padding exceed
   * it). */
  mono-wind:where([data-mw-no-regions]) input:not([type="checkbox"], [type="radio"]),
  mono-wind:where([data-mw-no-regions]) select,
  mono-wind:where([data-mw-regions])
    input:not([type="checkbox"], [type="radio"]):where(:not([data-mw-native], [data-mw-native] *)),
  mono-wind:where([data-mw-regions]) select:where(:not([data-mw-native], [data-mw-native] *)) {
    white-space: nowrap;
    overflow: hidden;
  }

  /* Textarea: engine controls the size (it auto-grows to fit the
   * value, so scrolling never has anything to reveal) — the native
   * resize handle and scrollbars would drag the box off-grid. `clip`
   * (not `hidden`) so the caret can't scroll the box either: under
   * `leading-*` the last line's half-leading is a phantom scroll
   * range that would shift every line off its row. */
  mono-wind:where([data-mw-no-regions]) textarea,
  mono-wind:where([data-mw-regions]) textarea:where(:not([data-mw-native], [data-mw-native] *)) {
    resize: none;
    overflow: clip;
  }

  /* A top-layer element is a surface of the host's, as the UA makes
   * it one of the canvas's (specs/top-layer.md): the derived tokens in
   * place of Canvas and CanvasText; `bg-clear` sees through. */
  mono-wind:where([data-mw-no-regions]) [popover],
  mono-wind:where([data-mw-no-regions]) dialog,
  mono-wind:where([data-mw-regions]) [popover]:where(:not([data-mw-native] *)),
  mono-wind:where([data-mw-regions]) dialog:where(:not([data-mw-native] *)) {
    color: var(--mw-fg);
    background-color: var(--mw-bg);
  }

  /* :focus-visible on ANY element: TUI-native invert. Outlines off
   * (they'd clip against grid glyphs). Where each declaration lands:
   * bg only shows via the grid (engine reads it during [measuring];
   * the light-DOM bg is transparent-locked below); color/caret-color
   * paint on editables (they're the ones exempted from the fill
   * lock), and get grid-mirrored by the engine for everyone else. A
   * composite's container (a menu, a listbox, `data-mw-composite`,
   * element.ts) has its items for its focus indication. */
  mono-wind:where([data-mw-no-regions]) :focus-visible,
  mono-wind:where([data-mw-regions]) :focus-visible:where(:not([data-mw-native] *)) {
    outline: none;
  }
  mono-wind:where([data-mw-no-regions]) :focus-visible:not([data-mw-composite]),
  mono-wind:where([data-mw-regions])
    :focus-visible:not([data-mw-composite]):where(:not([data-mw-native] *)) {
    background-color: var(--mw-fg);
    color: var(--mw-bg);
    caret-color: var(--mw-bg);
  }
  /* Grid-mode light elements carry the grid's text cursor: Firefox
   * hit-tests a multicol spanner's anonymous wrapper as its container
   * despite `pointer-events: none`, and would show an arrow there. The
   * interactives (specs/cell-model.md "Pointer states") keep their own,
   * and a native region its contents'. */
  mono-wind[select="grid"] [data-mw-laid-out]:not([data-mw-interactive], [data-mw-native]),
  mono-wind[select="grid"] [data-mw-flow]:not([data-mw-interactive], [data-mw-native]),
  mono-wind[select="grid"] [data-mw-float]:not([data-mw-interactive], [data-mw-native]) {
    cursor: text;
  }

  /* A native region's contents start native (specs/native-regions.md):
   * no lock reaches them, but they inherit from the locked boxes around
   * them, so the region takes each inherited lock back to CSS's initial
   * value, the host's font and color still reaching it, and an authored
   * `pointer-events: none` above it too — the browser's own value where
   * its sheet sets one. In `base`, so utilities restyle it. */
  mono-wind [data-mw-native] {
    -webkit-text-fill-color: currentColor;
    font-synthesis: initial;
    white-space: normal;
    overflow-wrap: normal;
    letter-spacing: normal;
    line-height: normal;
    text-indent: 0;
    orphans: initial;
    widows: initial;
  }
  mono-wind [data-mw-native]:where(pre, textarea, select) {
    white-space: revert;
  }
  mono-wind [data-mw-native]:where(textarea) {
    overflow-wrap: revert;
  }
  mono-wind[select="grid"] [data-mw-native]:where(:not([data-mw-pointer-none] *)) {
    pointer-events: auto;
    user-select: text;
    -webkit-user-select: text;
    cursor: auto;
  }
  mono-wind[select="grid"]
    [data-mw-native]:where(a:any-link, button, input, select, textarea, label) {
    cursor: revert;
  }
  mono-wind[select="grid"]
    [data-mw-native]:where(select, input:is([type="button"], [type="submit"], [type="reset"])) {
    user-select: revert;
    -webkit-user-select: revert;
  }
  /* Chromium and WebKit hand a parent's `::selection` down (css-pseudo-4
   * highlight inheritance), the selection lock's transparent: the region
   * names the system's. */
  mono-wind [data-mw-native]::selection {
    color: HighlightText;
    background-color: Highlight;
  }
}

/* === Modes and selection (engine-owned from here down) =============== */

/* select="grid": pointer events pass through the light DOM so a drag
 * lands on the grid <pre>; the interactives (`data-mw-interactive`,
 * element.ts) opt back in for clicks, except during a grid drag, so the
 * selection sweeps through their cells, and except where the grid
 * covers one (`data-mw-covered`, element.ts): the box painted over it
 * takes the pointer, hover and cursor included (specs/cell-model.md
 * "Pointer states"), its own subtree with it. These rules must be
 * important (a library writes inline pointer-events on its layers) and
 * unlayered, out of the lock layer (an author's `pointer-events-none!`
 * or `pointer-events-auto!` still wins), the cursor rule layered and
 * plain (an author's cursor wins). An authored `none` stays one: the
 * engine reads it with the locks off, under each element's measuring
 * flag, and marks it `data-mw-pointer-none`, which every opt-in here
 * leaves be. */
mono-wind[select="grid"]:where([data-mw-no-regions]) *:not([data-mw-measuring]),
mono-wind[select="grid"]:where([data-mw-regions])
  *:not([data-mw-measuring]):where(:not([data-mw-native], [data-mw-native] *)) {
  pointer-events: none !important;
}
/* A pseudo-element the engine reads takes its element's, whatever its
 * own. */
mono-wind[select="grid"] [data-mw-before]:not([data-mw-measuring])::before,
mono-wind[select="grid"] [data-mw-after]:not([data-mw-measuring])::after {
  pointer-events: inherit !important;
}
mono-wind[select="grid"]:not([data-mw-dragging])
  [data-mw-interactive]:not(
    [data-mw-covered],
    [data-mw-covered] *,
    [data-mw-measuring],
    [data-mw-pointer-none]
  ) {
  pointer-events: auto !important;
}
/* A grid drag sweeps through a native region too, its contents giving
 * up the pointer as the interactives do (specs/native-regions.md
 * "Interaction"): flagged on the region under a name of its own, so a
 * drag restyles regions' contents alone. */
mono-wind[select="grid"] [data-mw-native-drag],
mono-wind[select="grid"] [data-mw-native-drag] * {
  pointer-events: none !important;
}
/* A modal dialog blocks the page, the grid included (specs/top-layer.md
 * deviation 7): inside it, grid mode falls back to the light DOM's own
 * pointer events and selection, the highlight showing over the grid as
 * in text mode. Plain descendant selectors, so the style engine's
 * ancestor filter rejects every element outside a dialog at once
 * (architecture/performance.md). */
mono-wind[select="grid"]:where([data-mw-no-regions])
  dialog:modal:not([data-mw-measuring], [data-mw-pointer-none]),
mono-wind[select="grid"]:where([data-mw-regions])
  dialog:modal:not([data-mw-measuring], [data-mw-pointer-none]):where(
    :not([data-mw-native], [data-mw-native] *)
  ),
mono-wind[select="grid"]:where([data-mw-no-regions])
  dialog:modal
  :not([data-mw-measuring], [data-mw-pointer-none]),
mono-wind[select="grid"]:where([data-mw-regions])
  dialog:modal
  :not([data-mw-measuring], [data-mw-pointer-none]):where(
    :not([data-mw-native], [data-mw-native] *)
  ) {
  pointer-events: auto !important;
}
mono-wind[select="grid"]:where([data-mw-no-regions]) dialog:modal,
mono-wind[select="grid"]:where([data-mw-regions])
  dialog:modal:where(:not([data-mw-native], [data-mw-native] *)),
mono-wind[select="grid"]:where([data-mw-no-regions]) dialog:modal *,
mono-wind[select="grid"]:where([data-mw-regions])
  dialog:modal
  *:where(:not([data-mw-native], [data-mw-native] *)) {
  user-select: text;
  -webkit-user-select: text;
}
/* The grid-mode selection lock, explicit on the light DOM: the shadow
 * slot's `user-select: none` reaches slotted content in Chromium and
 * WebKit but not Firefox, where a drag that strays onto a light element
 * would select it. Lifted with a semantic selection
 * (specs/semantic-selection.md). Editable controls keep their own
 * selection (Firefox honors an explicit `none` on a text field's
 * value; the slot's inherited value never reached it). `:where()` keeps
 * the rule at base specificity so the coarse-pointer rule below wins.
 * The host's own text (specs/host-leaf.md) takes the host's value in
 * Firefox, so the leaf host is locked too. An editable's subtree
 * reverts to the layered cascade in a plain descendant rule, which
 * the ancestor filter rejects outside an editable
 * (architecture/performance.md). */
mono-wind[select="grid"]:where(:not([data-mw-semantic-selection]))[data-mw-leaf],
mono-wind[select="grid"]:where(:not([data-mw-semantic-selection])):where([data-mw-no-regions])
  :where(:not(input, textarea, [contenteditable]:not([contenteditable="false"]))),
mono-wind[select="grid"]:where(:not([data-mw-semantic-selection])):where([data-mw-regions])
  :where(:not(input, textarea, [contenteditable]:not([contenteditable="false"]))):where(
    :not([data-mw-native], [data-mw-native] *)
  ) {
  user-select: none;
  -webkit-user-select: none;
}
mono-wind[select="grid"]:where(:not([data-mw-semantic-selection])):where([data-mw-no-regions])
  [contenteditable]:not([contenteditable="false"])
  *,
mono-wind[select="grid"]:where(:not([data-mw-semantic-selection])):where([data-mw-regions])
  [contenteditable]:not([contenteditable="false"])
  *:where(:not([data-mw-native], [data-mw-native] *)) {
  user-select: revert-layer;
  -webkit-user-select: revert-layer;
}
/* Touch panning (specs/scrolling.md): where the primary pointer is a
 * finger, a scroll container's subtree takes pointer events in grid
 * mode so a drag pans it NATIVELY (physics, momentum, chaining — the
 * pan reaches the container from any descendant) and a long-press
 * selects the element's text under the finger (an element selection,
 * copied by the engine like a semantic one). The leaves must take the
 * touch themselves: WebKit resolves the pressed text position inside
 * the touched element, and on the container it falls back to the
 * first positioned leaf. */
@media (pointer: coarse) {
  mono-wind[select="grid"] [data-mw-scroll]:not([data-mw-measuring], [data-mw-pointer-none]),
  mono-wind[select="grid"]:where([data-mw-no-regions])
    [data-mw-scroll]
    :not([data-mw-measuring], [data-mw-pointer-none]),
  mono-wind[select="grid"]:where([data-mw-regions])
    [data-mw-scroll]
    :not([data-mw-measuring], [data-mw-pointer-none]):where(
      :not([data-mw-native], [data-mw-native] *)
    ) {
    pointer-events: auto !important;
  }
  mono-wind[select="grid"] [data-mw-scroll],
  mono-wind[select="grid"]:where([data-mw-no-regions]) [data-mw-scroll] *,
  mono-wind[select="grid"]:where([data-mw-regions])
    [data-mw-scroll]
    *:where(:not([data-mw-native], [data-mw-native] *)) {
    user-select: text;
    -webkit-user-select: text;
  }
}

/* Scroll containers (specs/scrolling.md): the gutter ink IS the
 * scrollbar — native bars hidden. Measuring-gated so the authored
 * scrollbar-width/-color stay readable during the style pass. */
@layer theme {
  mono-wind [data-mw-scroll]:not([data-mw-measuring]) {
    scrollbar-width: none !important;
    /* The engine's own writes — a thumb drag, a position restored after
     * a relayout, a routed wheel tick — land at once. */
    scroll-behavior: auto !important;
    /* The border and bar cells are native padding, inside the scrollport:
     * a reveal (focus, scrollIntoView) stops clear of them, and of the
     * authored scroll-padding past them, in cells. */
    scroll-padding: calc((var(--mw-bt, 0) + var(--mw-spt, 0)) * var(--mw-ch, 1px))
      calc((var(--mw-br, 0) + var(--mw-gr, 0) + var(--mw-spr, 0)) * var(--mw-cw, 1px))
      calc((var(--mw-bb, 0) + var(--mw-gb, 0) + var(--mw-spb, 0)) * var(--mw-ch, 1px))
      calc((var(--mw-bl, 0) + var(--mw-spl, 0)) * var(--mw-cw, 1px)) !important;
  }
  mono-wind [data-mw-scroll]:not([data-mw-measuring])::-webkit-scrollbar {
    display: none !important;
  }
}
/* The scroll-range spacer: pins the native scrollable overflow to the
 * ENGINE's range (--mw-se-* = max + box, written by render.ts) so
 * scrollTop can reach exactly the engine's max — browsers disagree
 * about end padding in scrollable overflow, and without this the
 * native ceiling can sit below the engine's, stranding the last
 * rows. It ends EXACTLY at the edge, no overhang: any native room
 * past the max would latch a native (text-mode) gesture to an
 * invisible scroll. Rounding can still put the ceiling a pixel either
 * side of max, so the engine reads "at the ceiling" as "at max". An
 * axis without range parks the spacer in the first cell: at −1px,
 * outside the padding box, it would leave the overflow area on both
 * axes. It takes the pseudo-element the author leaves free
 * (specs/generated-content.md "The light DOM"). */
mono-wind [data-mw-scroll="after"]:not([data-mw-measuring])::after,
mono-wind [data-mw-scroll="before"]:not([data-mw-measuring])::before {
  content: "";
  position: absolute;
  pointer-events: none;
  left: calc(var(--mw-se-x, 0) * var(--mw-cw, 1px) - 1px);
  top: calc(var(--mw-se-y, 0) * var(--mw-ch, 1px) - 1px);
  width: 1px;
  height: 1px;
}

/* Declared so it does NOT inherit: only the host reads it, and the
 * layout writes it after the grid is painted, where an inherited custom
 * property restyles every span below (architecture/performance.md). */
@property --mw-host-w {
  syntax: "*";
  inherits: false;
}

/* A native region's measuring width and clip (the
 * `data-mw-native-measure` and `data-mw-native-clip` rules): declared
 * so they do not inherit, a write restyling the region alone. */
@property --mw-measure-w {
  syntax: "*";
  inherits: false;
}
@property --mw-clip {
  syntax: "*";
  inherits: false;
}

/* A checkbox's or a radio's state, which its lock transitions
 * (specs/checkboxes.md): an integer, so a flip is a change to run. */
@property --mw-checked {
  syntax: "<integer>";
  inherits: false;
  initial-value: 0;
}

/* The host's used width is a whole number of cells (specs/cell-model.md
 * "Host sizing"): the engine measures the natural CSS width with this
 * rule lifted, then caps the box to the columns that fit — so borders,
 * backgrounds, and `mx-auto` centering land on the grid. A cap, not a
 * width, so a shrinking container still shrinks the host natively;
 * !important because the cap never exceeds the natural width it was
 * measured from, whatever max-width the author set (inline included). */
@layer theme {
  mono-wind:not([measuring]) {
    max-width: var(--mw-host-w) !important;
  }
}

/* Hide the host until the first layout completes to avoid the flash of raw
 * browser-rendered content (flex without our decorations, elements at their
 * natural sizes). `visibility: hidden` preserves layout & measurability so
 * cell metrics can still be probed while invisible. While the engine
 * reads, the host is visible instead: `visibility` inherits, and the
 * subtree reads as if the host were shown, the host's own visibility the
 * grid's natively (specs/visibility.md). A nested host never lays out —
 * its content is the outer host's to paint — so the rules skip it; a
 * host in another's native region is a host of its own
 * (specs/native-regions.md "Nesting"). */
@layer theme {
  mono-wind:not([data-mw-ready], [measuring], [data-mw-nested]) {
    visibility: hidden !important;
  }
  mono-wind[measuring] {
    visibility: visible !important;
  }
}

/* A light-DOM selection is painted on the grid — inverted cells from the
 * live range (specs/wide-characters.md "The grid paints the selection")
 * — so the browser's own highlight on the invisible native text stays
 * invisible: every field, as some engines keep an OS default background
 * when only `color` is set, and Safari's text-shadow ink. Only while one
 * is live or starting, under the host's `data-mw-selection` (element.ts;
 * why, architecture/performance.md). Shadow scopes can't share the
 * rule: element.ts (the slot) and the transcripts of @monowind/ascii
 * and @monowind/qr-code
 * restate it, on one element each and so ungated — update them
 * together. Forced colors paint selections themselves and strip
 * backgrounds, so the native highlight stays there. */
@media (forced-colors: none) {
  @layer theme {
    mono-wind:where([data-mw-selection]):not([measuring])[data-mw-leaf]::selection,
    mono-wind:where([data-mw-selection])[data-mw-no-regions]
      :where(:not(input, textarea, select, [contenteditable]:not([contenteditable="false"]))):not(
        [data-mw-measuring]
      )::selection,
    mono-wind:where([data-mw-selection])[data-mw-regions]
      :where(:not(input, textarea, select, [contenteditable]:not([contenteditable="false"]))):not(
        [data-mw-measuring]
      ):where(:not([data-mw-native], [data-mw-native] *))::selection {
      color: transparent !important;
      text-shadow: none !important;
      background: transparent !important;
    }
    /* An editable's subtree takes the swap below, in a plain descendant
     * rule as the user-select lock's, over the lock on specificity (its
     * `:where()`s). */
    mono-wind[data-mw-no-regions]
      [contenteditable]:not([contenteditable="false"])
      :not([data-mw-measuring])::selection,
    mono-wind[data-mw-regions]
      [contenteditable]:not([contenteditable="false"])
      :not([data-mw-measuring]):where(:not([data-mw-native], [data-mw-native] *))::selection {
      color: var(--mw-ground, var(--mw-bg, canvas)) !important;
      background: var(--mw-ink, var(--mw-fg, canvastext)) !important;
    }
  }
  /* An editable renders its own text, and Chromium and WebKit hand it
   * its parent's `::selection` (css-pseudo-4 highlight inheritance —
   * transparent, from the rule above): its own rule swaps its colors as
   * the grid swaps a selected cell's, from the ink and ground the engine
   * measured and wrote on it (render.ts). One selector per element kind,
   * so each is matched only against its own. */
  mono-wind:where([data-mw-no-regions]) input:not([data-mw-measuring])::selection,
  mono-wind:where([data-mw-regions])
    input:not([data-mw-measuring]):where(:not([data-mw-native], [data-mw-native] *))::selection,
  mono-wind:where([data-mw-no-regions]) textarea:not([data-mw-measuring])::selection,
  mono-wind:where([data-mw-regions])
    textarea:not([data-mw-measuring]):where(:not([data-mw-native], [data-mw-native] *))::selection,
  mono-wind:where([data-mw-no-regions]) select:not([data-mw-measuring])::selection,
  mono-wind:where([data-mw-regions])
    select:not([data-mw-measuring]):where(:not([data-mw-native], [data-mw-native] *))::selection,
  mono-wind:where([data-mw-no-regions])
    [contenteditable]:not([contenteditable="false"]):not([data-mw-measuring])::selection,
  mono-wind:where([data-mw-regions])
    [contenteditable]:not([contenteditable="false"]):not([data-mw-measuring]):where(
      :not([data-mw-native], [data-mw-native] *)
    )::selection {
    color: var(--mw-ground, var(--mw-bg, canvas));
    background: var(--mw-ink, var(--mw-fg, canvastext));
  }
}

/* === Typography locks and measuring gates ============================ */

/* A gate on a light element is the element's own flag — `data-mw-measuring`,
 * which the layout sets on every light element as it sets the host's
 * `measuring`, and `data-mw-settling`, on an element with a transition
 * to mask as the host's `settling` is — never the host's attribute read
 * through a descendant combinator: that would make each flip of the
 * host's attribute invalidate its whole subtree, the shadow grid's
 * every span walked at each flip. The host's attributes gate the host's
 * own rules alone. An element inserted since the last layout has no
 * flag, and so takes the locks until its first. */
@layer theme {
  /* Lock the font on descendants so the grid metrics stay consistent —
   * `!important` because user classes (`.text-2xl` etc.) would otherwise beat
   * these element-selector rules on specificity. */
  mono-wind[data-mw-no-regions] *,
  mono-wind[data-mw-regions] *:where(:not([data-mw-native], [data-mw-native] *)),
  mono-wind [data-mw-before]::before,
  mono-wind [data-mw-after]::after {
    font-family: inherit !important;
    font-size: inherit !important;
  }

  /* Lock toggles must never become native animations: an authored
   * `transition` would otherwise ANIMATE them — a background flashes to
   * its real value while `[measuring]` lifts the lock, and the snap back
   * to transparent starts a native fade that paints the light-DOM
   * element ON TOP of the grid (transitions beat even `!important`
   * declarations in the cascade). Restricting `transition-property` to
   * the SAMPLED properties (which no lock toggles — color stays live
   * under the text-fill lock, and nothing touches border colors,
   * opacity, or a layer's transforms and filter) snaps everything
   * lock-owned (backgrounds, geometry, backdrop-filter) while keeping
   * in-flight fades
   * of the sampled properties alive ACROSS measuring passes. On an
   * element (or the host) with a non-zero transition duration or delay
   * the engine swaps `[measuring]` for `[settling]` and forces one style
   * flush before unmasking, so the snap-back delta always commits
   * masked; an element with neither has no transition to start and
   * drops its flag alone. Outside both attributes the authored list is
   * live and fully respected — background fades, which can never run
   * natively, are synthesized by the engine instead. */
  mono-wind[measuring],
  mono-wind [data-mw-measuring],
  mono-wind [data-mw-before][data-mw-measuring]::before,
  mono-wind [data-mw-after][data-mw-measuring]::after,
  mono-wind[settling],
  mono-wind [data-mw-settling],
  mono-wind [data-mw-before][data-mw-settling]::before,
  mono-wind [data-mw-after][data-mw-settling]::after {
    transition-property:
      color, border-color, opacity, transform, translate, rotate, scale, filter !important;
  }

  /* A checkbox's or a radio's widget never paints nor sizes its box,
   * and its state's 1ms transition, after the author's own, tells the
   * host of a flip; the engine's read of its own styles lifts both
   * (specs/checkboxes.md "The light DOM"). */
  mono-wind[data-mw-no-regions]
    input:is([type="checkbox"], [type="radio"]):not([data-mw-control-read]),
  mono-wind[data-mw-regions]
    input:is([type="checkbox"], [type="radio"]):not([data-mw-control-read]):where(
      :not([data-mw-native], [data-mw-native] *)
    ) {
    appearance: none !important;
  }
  mono-wind[data-mw-no-regions]
    input:is([type="checkbox"], [type="radio"]):not(
      [data-mw-measuring],
      [data-mw-settling],
      [data-mw-control-read]
    ),
  mono-wind[data-mw-regions]
    input:is([type="checkbox"], [type="radio"]):not(
      [data-mw-measuring],
      [data-mw-settling],
      [data-mw-control-read]
    ):where(:not([data-mw-native], [data-mw-native] *)) {
    transition-property: var(--mw-tp, --mw-checked), --mw-checked !important;
    transition-duration: var(--mw-td, 0s), 1ms !important;
    transition-timing-function: var(--mw-tf, linear), linear !important;
    transition-delay: var(--mw-tl, 0s), 0s !important;
    transition-behavior: var(--mw-tb, normal), normal !important;
  }

  /* Anchor names scoped to their own subtree while the engine reads: an
   * anchored box then resolves no anchor natively, so the browser applies
   * no position-try fallback of its own (from its pixel geometry, not the
   * cells') and `position-area` reads as authored
   * (specs/anchor-positioning.md). On the elements that name one alone
   * (`data-mw-anchor`, element.ts): on every element the property keeps
   * Chromium from sharing any read style between them
   * (architecture/performance.md). */
  mono-wind [data-mw-anchor][data-mw-measuring] {
    anchor-scope: all !important;
  }

  /* A popover's or dialog's exit rides a discrete display transition
   * (specs/top-layer.md): on them, and them alone, the mask keeps
   * display and overlay alive across the passes — no lock toggles either
   * there, while the grid-template read assist below toggles display. */
  mono-wind [popover][data-mw-measuring],
  mono-wind dialog[data-mw-measuring],
  mono-wind [popover][data-mw-settling],
  mono-wind dialog[data-mw-settling] {
    transition-property:
      color, border-color, opacity, transform, translate, rotate, scale, filter, display, overlay !important;
  }

  /* Grid-template read assist (specs/grid.md), no-Typed-OM engines only
   * (Firefox pre-157): getComputedStyle on a live grid container returns
   * the USED `grid-template-*` track list (expanded px) — the authored
   * fr/repeat/minmax structure is gone. The style reader sets this
   * attribute for the duration of the read; blockifying the element makes
   * getComputedStyle return the COMPUTED value, structure intact.
   * `!important` so inline `style="display: grid"` can't defeat the read. */
  mono-wind [data-mw-degrid][data-mw-measuring] {
    display: block !important;
  }

  /* An out-of-flow element's display before its position blockified it
   * (specs/positioning.md "Static position"), read under this flag for
   * the one read. */
  mono-wind [data-mw-static-read][data-mw-measuring],
  mono-wind [data-mw-before-static-read][data-mw-measuring]::before,
  mono-wind [data-mw-after-static-read][data-mw-measuring]::after {
    position: static !important;
  }

  /* A pseudo-element with no box for the one read of its lengths as
   * computed (style.ts `declaredLengths`): `contents`, which keeps its
   * animations running where `none` would end them. */
  mono-wind [data-mw-before-declared][data-mw-measuring]::before,
  mono-wind [data-mw-after-declared][data-mw-measuring]::after {
    display: contents !important;
    position: static !important;
    float: none !important;
  }

  /* An image's picture is the grid's (specs/images.md): the native one is
   * a transparent pixel, while the element keeps its box, hit target and
   * alt, and `drawImage` still reads its source. */
  mono-wind[data-mw-no-regions] img:not([data-mw-measuring]),
  mono-wind[data-mw-regions]
    img:not([data-mw-measuring]):where(:not([data-mw-native], [data-mw-native] *)) {
    content: url("data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7") !important;
  }

  /* Grid-quantized typography and the wrap model (specs/cell-model.md). Gated
   * on the measuring flag (unlike the font lock above) so the style reader
   * sees the AUTHORED white-space / letter-spacing / line-height — that's how nowrap,
   * tracking, and leading are detected without echoing our own output.
   *
   * letter-spacing: the root's own (`--mw-rls`, part of the cell) plus
   * `--mw-ls` extra cells per character, which the engine writes on every
   * element it visits; an element without the var makes this declaration
   * invalid at computed-value time, so the property falls back to inheriting
   * the parent's grid-exact value. line-height is inherited from the
   * laid-out ancestor (which sets the row multiple below) — `leading-*` on
   * inline elements is ignored by design. */
  mono-wind[data-mw-no-regions] :not([data-mw-measuring]),
  mono-wind[data-mw-regions]
    :not([data-mw-measuring]):where(:not([data-mw-native], [data-mw-native] *)) {
    /* Baseline-shift lock (cell-model): gated like the rest, so the style
     * reader sees the authored/UA vertical-align (table cells read it). */
    vertical-align: baseline !important;
    white-space: normal !important;
    overflow-wrap: anywhere !important;
    letter-spacing: calc(var(--mw-rls, 0px) + var(--mw-ls) * var(--mw-cw, 1px)) !important;
    line-height: inherit !important;
    text-shadow: none !important;
    /* Native markers take no line space, the grid drawing its own: an
     * inside one's cells are its line's indent (--mw-ti, specs/lists.md
     * "The light DOM"). An image, which no font size shrinks, goes. */
    list-style-position: outside !important;
    list-style-image: none !important;
  }
  /* What the grid paints of a box, a native region's included. */
  mono-wind[data-mw-no-regions] :not([data-mw-measuring]),
  mono-wind[data-mw-regions] :not([data-mw-measuring]):where(:not([data-mw-native] *)) {
    /* Backgrounds paint in the grid too, so the light DOM's own bg goes
     * transparent — otherwise it would cover the grid (light DOM sits
     * ON TOP of the grid in the shadow tree so native selection
     * highlights can show through). The engine still reads authored
     * background-color during [measuring] (this rule is off then), so
     * bg-* and focus-invert both make it into the grid paint. */
    background-color: transparent !important;
    /* Gradients paint in the grid as a color per cell (specs/gradients.md),
     * read during [measuring] like the color. */
    background-image: none !important;
    /* Shadows paint in the grid as shades (specs/box-shadow.md), read
     * during [measuring] like backgrounds. */
    box-shadow: none !important;
    /* Borders are glyphs the grid draws on boxes (specs/cell-model.md):
     * a native one would draw off the grid, and an inline element's, which
     * the engine ignores, would shift its line's native text. */
    border-width: 0 !important;
    /* Engine variables a rule can read on an element that didn't write
     * them, reset plainly (specs/cell-model.md "Engine variables"). */
    --mw-z: initial;
    --mw-it: initial;
    --mw-ir: initial;
    --mw-ib: initial;
    --mw-il: initial;
    --mw-ipl: 0;
    --mw-ipr: 0;
    --mw-sx: 0;
    --mw-sy: 0;
    --mw-vb: 0;
    /* Inline-element horizontal padding, quantized: the engine writes
     * `--mw-ipl`/`--mw-ipr` (whole cells) on inline elements whose authored
     * padding it reserved in the run; everything else gets 0, so raw
     * off-grid padding never renders. Laid-out elements and atomic inline
     * boxes override this with their own engine-owned padding rule
     * (below). Vertical inline padding never moves layout,
     * per CSS, and passes through untouched. */
    padding-left: calc(var(--mw-ipl) * var(--mw-cw, 1px)) !important;
    padding-right: calc(var(--mw-ipr) * var(--mw-cw, 1px)) !important;
  }

  /* A `::before` or `::after` the engine reads, its kind in its
   * element's flag (render.ts), paints no ink of its own — its element's
   * fill lock reaches its text by inheritance — and takes the grid's
   * typography and the cells its run gave its padding and margins
   * (specs/generated-content.md "The light DOM"). */
  mono-wind [data-mw-before]:not([data-mw-measuring])::before,
  mono-wind [data-mw-after]:not([data-mw-measuring])::after {
    background-color: transparent !important;
    background-image: none !important;
    text-shadow: none !important;
    box-shadow: none !important;
    border-width: 0 !important;
    text-decoration-color: transparent !important;
    vertical-align: baseline !important;
    white-space: inherit !important;
    line-height: inherit !important;
  }
  mono-wind [data-mw-before]:not([data-mw-measuring])::before {
    letter-spacing: calc(var(--mw-rls, 0px) + var(--mw-before-ls) * var(--mw-cw, 1px)) !important;
    padding: 0 calc(var(--mw-before-pr) * var(--mw-cw, 1px)) 0
      calc(var(--mw-before-pl) * var(--mw-cw, 1px)) !important;
    margin: 0 calc(var(--mw-before-mr) * var(--mw-cw, 1px)) 0
      calc(var(--mw-before-ml) * var(--mw-cw, 1px)) !important;
  }
  mono-wind [data-mw-after]:not([data-mw-measuring])::after {
    letter-spacing: calc(var(--mw-rls, 0px) + var(--mw-after-ls) * var(--mw-cw, 1px)) !important;
    padding: 0 calc(var(--mw-after-pr) * var(--mw-cw, 1px)) 0
      calc(var(--mw-after-pl) * var(--mw-cw, 1px)) !important;
    margin: 0 calc(var(--mw-after-mr) * var(--mw-cw, 1px)) 0
      calc(var(--mw-after-ml) * var(--mw-cw, 1px)) !important;
  }
  /* A block one in a multi-column flow keeps its text, its native lines
   * fragmenting as the grid's do (`data-mw-multicol-flow`), with its
   * vertical margins, or the padding they ride as. */
  mono-wind [data-mw-before="flow"]:not([data-mw-measuring])::before {
    padding-block: calc(var(--mw-before-pt) * var(--mw-ch, 1px))
      calc(var(--mw-before-pb) * var(--mw-ch, 1px)) !important;
    margin-block: calc(var(--mw-before-mt) * var(--mw-ch, 1px))
      calc(var(--mw-before-mb) * var(--mw-ch, 1px)) !important;
  }
  mono-wind [data-mw-after="flow"]:not([data-mw-measuring])::after {
    padding-block: calc(var(--mw-after-pt) * var(--mw-ch, 1px))
      calc(var(--mw-after-pb) * var(--mw-ch, 1px)) !important;
    margin-block: calc(var(--mw-after-mt) * var(--mw-ch, 1px))
      calc(var(--mw-after-mb) * var(--mw-ch, 1px)) !important;
  }
  /* A box draws no text natively, its box the engine's cells: absolute
   * where the engine places it, else in its line or flow, with its
   * margins. */
  mono-wind [data-mw-before="box"]:not([data-mw-measuring])::before,
  mono-wind [data-mw-after="box"]:not([data-mw-measuring])::after {
    content: "" !important;
    padding: 0 !important;
    min-width: 0 !important;
    min-height: 0 !important;
    max-width: none !important;
    max-height: none !important;
    position-area: none !important;
    grid-area: auto !important;
  }
  mono-wind [data-mw-before="box"]:not([data-mw-measuring])::before {
    position: var(--mw-before-position) !important;
    inset: calc(var(--mw-before-y) * var(--mw-ch, 1px)) auto auto
      calc(var(--mw-before-x) * var(--mw-cw, 1px)) !important;
    width: calc(var(--mw-before-w) * var(--mw-cw, 1px)) !important;
    height: calc(var(--mw-before-h) * var(--mw-ch, 1px)) !important;
    margin: calc(var(--mw-before-mt) * var(--mw-ch, 1px))
      calc(var(--mw-before-mr) * var(--mw-cw, 1px)) calc(var(--mw-before-mb) * var(--mw-ch, 1px))
      calc(var(--mw-before-ml) * var(--mw-cw, 1px)) !important;
    z-index: var(--mw-before-z) !important;
    vertical-align: var(--mw-before-va) !important;
    contain: var(--mw-before-contain) !important;
  }
  mono-wind [data-mw-after="box"]:not([data-mw-measuring])::after {
    position: var(--mw-after-position) !important;
    inset: calc(var(--mw-after-y) * var(--mw-ch, 1px)) auto auto
      calc(var(--mw-after-x) * var(--mw-cw, 1px)) !important;
    width: calc(var(--mw-after-w) * var(--mw-cw, 1px)) !important;
    height: calc(var(--mw-after-h) * var(--mw-ch, 1px)) !important;
    margin: calc(var(--mw-after-mt) * var(--mw-ch, 1px))
      calc(var(--mw-after-mr) * var(--mw-cw, 1px)) calc(var(--mw-after-mb) * var(--mw-ch, 1px))
      calc(var(--mw-after-ml) * var(--mw-cw, 1px)) !important;
    z-index: var(--mw-after-z) !important;
    vertical-align: var(--mw-after-va) !important;
    contain: var(--mw-after-contain) !important;
  }

  /* A native marker takes no ink, no hit — an outside one hangs past
   * its item's box, which the covered rule never guards — and no line
   * height, its zero-size box's baseline otherwise stretching the line
   * in Firefox and WebKit (specs/lists.md "The light DOM"); its type and
   * text stay for assistive technology. */
  mono-wind[data-mw-no-regions] *::marker,
  mono-wind[data-mw-regions] *:where(:not([data-mw-native] *))::marker {
    font-size: 0 !important;
    line-height: 0 !important;
  }

  /* A marker or pseudo-element whose `content` holds an image draws none
   * (render.ts), and a native region's own marker, which the grid draws
   * as a box's (its `list-style-image` left to its items), gated so the
   * engine reads the authored value. */
  mono-wind [data-mw-marker-image]:not([data-mw-measuring])::marker,
  mono-wind [data-mw-native]:not([data-mw-measuring])::marker,
  mono-wind [data-mw-before="image"]:not([data-mw-measuring])::before,
  mono-wind [data-mw-after="image"]:not([data-mw-measuring])::after {
    content: none !important;
  }

  /* A top-layer element's backdrop is a box of the grid's beneath its
   * own (specs/top-layer.md), read during [measuring] like backgrounds. */
  mono-wind[data-mw-no-regions] [popover]:not([data-mw-measuring])::backdrop,
  mono-wind[data-mw-regions]
    [popover]:not([data-mw-measuring]):where(:not([data-mw-native] *))::backdrop,
  mono-wind[data-mw-no-regions] dialog:not([data-mw-measuring])::backdrop,
  mono-wind[data-mw-regions]
    dialog:not([data-mw-measuring]):where(:not([data-mw-native] *))::backdrop {
    background: transparent !important;
    backdrop-filter: none !important;
  }

  /* Light-DOM text goes invisible so the grid is the only visible copy;
   * form controls opt out because they render their value/caret/selection
   * natively (the tree builder skips their content so the grid doesn't
   * double-render). Events, ARIA, focus, selection all still work.
   * `-webkit-text-fill-color` (universal support), NOT `color`: it hides
   * the ink while computed `color` stays LIVE — authored transitions run
   * on it for real and the engine samples true mid-fade values (the
   * animation loop, element.ts). Decoration ink follows `color`, so it
   * needs its own transparent lock (the grid paints decorations).
   * `[data-mw-leaf]` is the host with its own text on the grid — the root
   * leaf, or a mixed host's anonymous runs (specs/host-leaf.md): every
   * rule that shapes a leaf's native text carries this host variant —
   * render.ts writes the host's flags. */
  mono-wind:not([measuring])[data-mw-leaf],
  mono-wind[data-mw-no-regions] :not(input):not(textarea):not(select):not([data-mw-measuring]),
  mono-wind[data-mw-regions]
    :not(input):not(textarea):not(select):not([data-mw-measuring]):where(
      :not([data-mw-native], [data-mw-native] *)
    ) {
    -webkit-text-fill-color: transparent !important;
    text-decoration-color: transparent !important;
  }

  /* Form controls escape the fill lock by INHERITANCE, not just by the
   * exclusion above: text-fill-color inherits and has no UA default on
   * controls (unlike `color`), so without this reset an input under any
   * locked ancestor would inherit transparent ink. */
  mono-wind[data-mw-no-regions] input:not([data-mw-measuring]),
  mono-wind[data-mw-regions]
    input:not([data-mw-measuring]):where(:not([data-mw-native], [data-mw-native] *)),
  mono-wind[data-mw-no-regions] textarea:not([data-mw-measuring]),
  mono-wind[data-mw-regions]
    textarea:not([data-mw-measuring]):where(:not([data-mw-native], [data-mw-native] *)),
  mono-wind[data-mw-no-regions] select:not([data-mw-measuring]),
  mono-wind[data-mw-regions]
    select:not([data-mw-measuring]):where(:not([data-mw-native], [data-mw-native] *)) {
    -webkit-text-fill-color: currentColor !important;
  }

  /* Author-requested `white-space: nowrap` (or `pre`) — the engine sized this
   * box for unwrapped hard lines, so the browser must not wrap either. Wins
   * over the lock above. */
  mono-wind:not([measuring])[data-mw-nowrap],
  mono-wind [data-mw-nowrap]:not([data-mw-measuring]) {
    white-space: nowrap !important;
  }

  /* `white-space: pre` leaves (specs/cell-model.md), and `pre-line`,
   * `pre-wrap` and `break-spaces` ones through `--mw-ws`: the tree builder
   * kept the source's spaces or newlines in the run, so the browser must
   * render them too — on the leaf AND its inline descendants (the lock
   * above would collapse text inside nested spans). Later than the nowrap
   * rule, so it wins on the leaf itself at equal specificity. */
  mono-wind:not([measuring])[data-mw-pre],
  mono-wind[data-mw-pre][data-mw-no-regions] :not([data-mw-measuring]),
  mono-wind[data-mw-pre][data-mw-regions]
    :not([data-mw-measuring]):where(:not([data-mw-native], [data-mw-native] *)),
  mono-wind [data-mw-pre]:not([data-mw-measuring]),
  mono-wind[data-mw-no-regions] [data-mw-pre] *:not([data-mw-measuring]),
  mono-wind[data-mw-regions]
    [data-mw-pre]
    *:not([data-mw-measuring]):where(:not([data-mw-native], [data-mw-native] *)) {
    white-space: var(--mw-ws) !important;
  }

  /* Truncation (`truncate` = nowrap + hidden + text-overflow: ellipsis)
   * keeps `hidden` where the lock below writes `clip`: a clip box is no
   * scroll container, so focus can never reveal what the ellipsis hides
   * — a link past the cut stays off screen for a keyboard (measured:
   * Chromium and Firefox scroll it into view under `hidden`, neither
   * does under `clip`; WebKit reveals it under neither). `text-overflow`
   * itself works the same either way in all three. Wins over the
   * `[data-mw-clip]` rule below on specificity. */
  mono-wind [data-mw-nowrap][data-mw-clip]:not([data-mw-measuring]) {
    overflow: hidden !important;
  }

  /* === Engine-owned geometry =========================================== */

  /* Every CSS property the engine has taken over on laid-out elements.
   * Gated on the measuring flag so the style reader still sees the
   * authored values during its read pass. In the lock layer (the file's
   * header) the `!important` beats every utility, `!` modifier included,
   * inline `style=""` attributes (hand-written or CSS-in-JS), and
   * high-specificity unlayered author CSS.
   *
   * Geometry: position/box-sizing/left/top/width/height + min/max — set from
   * the engine-owned --mw-x/y/w/h custom properties.
   *
   * Neutralized: margin (position:absolute + margin still nudges the
   * element off-grid); border-width goes with every element's, above.
   *
   * Owned via `--mw-p{t,r,b,l}` (padding) and `--mw-b{t,r,b,l}` (border cells):
   * both must land on whole cells. The browser applies the sum as native
   * padding since we've zeroed native border-width — this way, content sits
   * inset by exactly the (border + padding) cells the engine planned for. */
  mono-wind [data-mw-laid-out]:not([data-mw-measuring]) {
    position: absolute !important;
    /* An anchored box is the engine's to place (specs/anchor-positioning.md):
     * the browser's own anchored placement stays off. */
    position-area: none !important;
    /* A sticky box's shift for the scroll, and the scroll a fixed box
     * takes back, ride beside its position (render.ts `writeShift`). */
    left: calc((var(--mw-x, 0) + var(--mw-sx)) * var(--mw-cw, 1px)) !important;
    /* `leading-*` on the grid: `--mw-lh` rows per wrapped line. CSS puts half
     * the extra leading ABOVE the first line; `--mw-lhs` (−(rows − 1)/2)
     * lifts the box back so every line starts on its own row. */
    top: calc((var(--mw-y, 0) + var(--mw-sy) + var(--mw-lhs, 0)) * var(--mw-ch, 1px)) !important;
    /* Neutralize the browser's residual grid machinery (specs/grid.md).
     * grid-area: per CSS §10.1 an ABSOLUTE child of a grid container with
     * placement properties uses its grid area as containing block — our
     * left/top would resolve against that area and double-offset the
     * element (the engine already placed it). */
    grid-area: auto !important;
  }
  /* The template and content/item alignment resets make a laid-out
   * container's own grid/flex inert for the one box the browser still
   * lays out: the anonymous item holding a leaf's or a text grid's
   * direct text (a template or an implicit track's size would trap it in
   * a track; the browser's place-* offsets are FRACTIONAL, off the cell
   * grid — the engine applies the quantized offsets through its owned
   * padding instead, specs/cell-model.md). A native region keeps its
   * own. */
  mono-wind [data-mw-laid-out]:not([data-mw-measuring], [data-mw-native]) {
    grid-template-columns: none !important;
    grid-template-rows: none !important;
    grid-auto-columns: auto !important;
    grid-auto-rows: auto !important;
    place-content: start !important;
    place-items: stretch !important;
  }

  /* A top-layer element's box is the viewport's (specs/top-layer.md):
   * its cells count from the grid's client origin, a `:host` rule of the
   * shadow's in px, kept current through page scrolls. */
  mono-wind [data-mw-laid-out][data-mw-top]:not([data-mw-measuring]) {
    position: fixed !important;
    left: calc(var(--mw-ox, 0px) + var(--mw-x, 0) * var(--mw-cw, 1px)) !important;
    top: calc(
      var(--mw-oy, 0px) + (var(--mw-y, 0) + var(--mw-lhs, 0)) * var(--mw-ch, 1px)
    ) !important;
  }

  /* Shared cell geometry for engine-sized boxes — engine-POSITIONED
   * elements, in-flow atomic inline boxes, and in-flow multicol spanners
   * (below). */
  mono-wind [data-mw-laid-out]:not([data-mw-measuring]),
  mono-wind [data-mw-inline-box]:not([data-mw-measuring]),
  mono-wind [data-mw-multicol-flow-span]:not([data-mw-measuring]),
  mono-wind [data-mw-flow]:not([data-mw-measuring]),
  mono-wind [data-mw-float]:not([data-mw-measuring]) {
    box-sizing: border-box !important;
    /* Trailing-gap allowance: a tracked box's lines end with a phantom
     * `--mw-ls`-cell gap the engine doesn't count. Give the content box that
     * room by carving it out of the engine-owned right padding/border cells;
     * only when those are fewer than the gap does the element box itself
     * widen (invisibly — the decorated border stays where the engine put it).
     * The extra 1/32px is one layout unit of headroom: engines store lengths
     * on a fixed grid (truncating to 1/64px in Chromium and WebKit, rounding
     * to 1/60px in Firefox), so `cells × cw` can land a unit BELOW the shaped
     * advance of a line that fits exactly, and the browser would wrap it.
     * Adding at least one unit guarantees the stored width is ≥ the exact
     * product; 1/32px covers every engine's unit and is far below a cell
     * (and below a device pixel), so it changes neither cell math nor
     * paint. */
    width: calc(
      (var(--mw-w, 0) + max(0, var(--mw-ls, 0) - var(--mw-pr, 0) - var(--mw-br, 0))) *
        var(--mw-cw, 1px) + 0.03125px
    ) !important;
    height: calc(var(--mw-h, 0) * var(--mw-ch, 1px)) !important;
    min-width: 0 !important;
    min-height: 0 !important;
    max-width: none !important;
    max-height: none !important;
    /* Authored z-index applies only where CSS says (positioned, flex/grid
     * items) — absolutization would otherwise activate it everywhere. The
     * engine writes --mw-z in exactly those cases; elsewhere the reset
     * leaves the calc() invalid at computed-value time and z-index falls
     * back to auto. */
    z-index: calc(var(--mw-z)) !important;
    /* A layer root's backdrop filter goes on its layer (specs/layers.md):
     * the element's own would take in the layer's cells beneath it. */
    backdrop-filter: none !important;
    padding-top: calc((var(--mw-pt, 0) + var(--mw-bt, 0)) * var(--mw-ch, 1px)) !important;
    padding-right: calc(
      max(0, var(--mw-pr, 0) + var(--mw-br, 0) - var(--mw-ls, 0)) * var(--mw-cw, 1px)
    ) !important;
    padding-bottom: calc((var(--mw-pb, 0) + var(--mw-bb, 0)) * var(--mw-ch, 1px)) !important;
    padding-left: calc((var(--mw-pl, 0) + var(--mw-bl, 0)) * var(--mw-cw, 1px)) !important;
    margin: 0 !important;
  }
  /* An engine-sized box's lines on its rows, and its gap rules the grid's
   * glyphs (specs/gap-decorations.md): css-gaps browsers must not also
   * paint their native (fractional) rules — e.g. between an emptied
   * grid's explicit tracks. A native region keeps its own. */
  mono-wind [data-mw-laid-out]:not([data-mw-measuring], [data-mw-native]),
  mono-wind [data-mw-inline-box]:not([data-mw-measuring], [data-mw-native]),
  mono-wind [data-mw-multicol-flow-span]:not([data-mw-measuring], [data-mw-native]),
  mono-wind [data-mw-flow]:not([data-mw-measuring], [data-mw-native]),
  mono-wind [data-mw-float]:not([data-mw-measuring], [data-mw-native]) {
    line-height: calc(var(--mw-lh, 1) * var(--mw-ch, 1px)) !important;
    column-rule-style: none !important;
    row-rule-style: none !important;
  }

  /* Atomic inline boxes (`inline-flex`/`inline-block`/`inline-grid` riding a
   * text run, specs/cell-model.md): the box stays IN FLOW — the browser's
   * own line layout places it, and both models agree because the engine
   * sizes it to exactly the cells its U+FFFC marker occupies in the wrap.
   * `position: relative` makes it the containing block for its laid-out
   * children; `vertical-align: top` pins the box's top to its line's first
   * row (the global baseline lock would hang an abspos-only box below the
   * line) — a box taller than one row grows its line, per CSS. */
  mono-wind [data-mw-inline-box]:not([data-mw-measuring]) {
    position: relative !important;
    /* A sticky inline box's shift for the scroll (specs/sticky.md; zero
     * otherwise) and its lift from where its line's leading puts it
     * (render.ts `inlineLift`), its native ink on its rows. */
    top: calc((var(--mw-sy) + var(--mw-lhs, 0)) * var(--mw-ch, 1px)) !important;
    left: calc(var(--mw-sx) * var(--mw-cw, 1px)) !important;
    vertical-align: top !important;
    /* Its margins are the engine's cells; its advance in the line is its
     * margin box: the right margin gives back the shared rule's widening,
     * else two boxes on an exactly full line outgrow the line's one unit
     * of headroom and the browser wraps the second where the grid does
     * not — and one Firefox unit more, the width and the margin each
     * rounding there to the nearest 1/60px. */
    margin: calc(var(--mw-mt, 0) * var(--mw-ch, 1px))
      calc(
        (var(--mw-mr, 0) - max(0, var(--mw-ls, 0) - var(--mw-pr, 0) - var(--mw-br, 0))) *
          var(--mw-cw, 1px) - 0.03125px - 0.0166667px
      )
      calc(var(--mw-mb, 0) * var(--mw-ch, 1px)) calc(var(--mw-ml, 0) * var(--mw-cw, 1px)) !important;
  }

  /* Authored `vertical-align: bottom` on an atomic inline box: grid-exact
   * in every engine (probed), so it passes through; the engine drops the
   * line's text to the box's last row to match. baseline is off-grid and
   * behaves as top (cell-model deviation). */
  mono-wind [data-mw-inline-box][data-mw-vbottom]:not([data-mw-measuring]) {
    vertical-align: bottom !important;
  }
  /* Authored `vertical-align: middle`: a whole-row baseline length the
   * engine writes (specs/cell-model.md "Typography") — rows from the
   * box's own baseline to its line's text row, plus the row's baseline
   * for a box whose baseline is its bottom edge — so the line's text
   * lands on the box's middle row in every engine (probed). */
  mono-wind [data-mw-inline-box][data-mw-vmiddle]:not([data-mw-measuring]) {
    vertical-align: calc(
      var(--mw-va, 0) * var(--mw-ch, 1px) + var(--mw-vb) * var(--mw-base, 0px)
    ) !important;
  }

  /* A native region's frame (specs/native-regions.md): its border cells
   * a transparent native border and its padding cells native padding,
   * so its own scrollbar and overflow clip sit inside the grid's border;
   * isolated, so its stacking holds clipped or not. An inline one is an
   * atomic inline box natively too. */
  mono-wind [data-mw-native]:not([data-mw-measuring]) {
    border-style: solid !important;
    border-color: transparent !important;
    border-width: calc(var(--mw-bt, 0) * var(--mw-ch, 1px))
      calc(var(--mw-br, 0) * var(--mw-cw, 1px)) calc(var(--mw-bb, 0) * var(--mw-ch, 1px))
      calc(var(--mw-bl, 0) * var(--mw-cw, 1px)) !important;
    padding: calc(var(--mw-pt, 0) * var(--mw-ch, 1px)) calc(var(--mw-pr, 0) * var(--mw-cw, 1px))
      calc(var(--mw-pb, 0) * var(--mw-ch, 1px)) calc(var(--mw-pl, 0) * var(--mw-cw, 1px)) !important;
    isolation: isolate !important;
  }
  mono-wind [data-mw-native="inline"]:not([data-mw-measuring]) {
    display: inline-block !important;
  }
  /* A region's contents measured as the browser lays them out
   * (specs/native-regions.md "Layout"): its content box alone at the
   * width the engine asks, its height theirs; out of flow, so its own
   * display blockifies and an inline one takes the width. */
  mono-wind [data-mw-native-measure] {
    position: absolute !important;
    inset: auto !important;
    width: var(--mw-measure-w) !important;
    height: auto !important;
    min-width: 0 !important;
    min-height: 0 !important;
    max-width: none !important;
    max-height: none !important;
    aspect-ratio: auto !important;
    box-sizing: content-box !important;
    padding: 0 !important;
    border-width: 0 !important;
    margin: 0 !important;
  }

  /* Multicol TEXT LEAF (specs/multicol.md "Browser agreement"): native
   * columns driven by the engine's used values — quantized count and gap,
   * sequential fill into the engine-quantized content height — so the
   * browser breaks on exactly the engine's lines and the native text,
   * caret, and selection sit under the grid's glyphs. The orphans/widows
   * initial value of 2 vetoes single-line column endings the engine's
   * model allows (probed: Chromium then breaks [3, 2, 2] where the engine
   * says [3, 3, 1]), so both reset to 1. */
  mono-wind [data-mw-multicol]:not([data-mw-measuring]) {
    column-count: var(--mw-colc, 1) !important;
    column-width: auto !important;
    column-gap: calc(var(--mw-colg, 0) * var(--mw-cw, 1px)) !important;
    column-fill: auto !important;
    orphans: 1 !important;
    widows: 1 !important;
    /* No trailing-gap carve-out here: it would widen only the LAST column
     * (native columns split the content box equally), so tracked multicol
     * text instead wraps at `width − tracking` engine-side — the exact
     * fit rule the browser applies per column. */
    width: calc(var(--mw-w, 0) * var(--mw-cw, 1px) + 0.03125px) !important;
    padding-right: calc((var(--mw-pr, 0) + var(--mw-br, 0)) * var(--mw-cw, 1px)) !important;
    /* Column-axis reconstruction: the engine's box is TIGHT (each
     * column's last trailing leading trimmed, like single-column leaves),
     * but browsers count FULL line boxes when fragmenting — re-extend by
     * the `--mw-lh − 1` leading rows so every column fits its lines.
     * `--mw-overhang` (the host-measured ink overhang — WebKit breaks at
     * ink bottoms, and some fonts draw taller than their line box) plus
     * one layout unit of calc-rounding slack keep the last line's ink
     * inside its column. The extension is under one line box, so no engine fits
     * an extra line, and the extra rows are blank leading — nothing
     * visible moves. */
    height: calc(
      (var(--mw-h, 0) + var(--mw-lh, 1) - 1) * var(--mw-ch, 1px) + var(--mw-overhang, 0px) +
        0.03125px
    ) !important;
  }

  /* Paragraph-flow multicol children (specs/multicol.md "Fragmenting
   * text-leaf children"): chrome-less text leaves stay IN FLOW inside the
   * container's native columns so the browser fragments them itself, with
   * engine-quantized margins (native sibling collapsing IS the engine's
   * collapse rule). Width and height stay natural — a chrome-less block's
   * natural height is full line boxes, exactly what fragmentation counts;
   * typography quantization and the color/background locks come from the
   * universal rules, and line-height inherits from the container. A
   * pseudo-element among them takes its margins from its own rule. */
  mono-wind [data-mw-multicol-flow]:not([data-mw-measuring]),
  mono-wind [data-mw-before="flow"]:not([data-mw-measuring])::before,
  mono-wind [data-mw-after="flow"]:not([data-mw-measuring])::after {
    position: static !important;
    display: block !important;
    box-sizing: border-box !important;
    width: auto !important;
    height: auto !important;
    min-width: 0 !important;
    min-height: 0 !important;
    max-width: none !important;
    max-height: none !important;
    column-span: none !important;
  }
  mono-wind [data-mw-multicol-flow]:not([data-mw-measuring]) {
    margin: calc(var(--mw-mt, 0) * var(--mw-ch, 1px)) calc(var(--mw-mr, 0) * var(--mw-cw, 1px))
      calc(var(--mw-mb, 0) * var(--mw-ch, 1px)) calc(var(--mw-ml, 0) * var(--mw-cw, 1px)) !important;
  }

  /* text-indent (specs/cell-model.md): native px would resolve to the
   * root font size, not the cell width, so the transparent light-DOM
   * text drifts out from under the grid's paint. Rewrite it in cells so
   * the two agree — selection ranges land exactly where the glyphs are.
   * A native region's text is native. */
  mono-wind:not([measuring])[data-mw-leaf],
  mono-wind [data-mw-laid-out]:not([data-mw-measuring], [data-mw-native]),
  mono-wind [data-mw-multicol-flow]:not([data-mw-measuring]),
  mono-wind [data-mw-flow]:not([data-mw-measuring], [data-mw-native]),
  mono-wind [data-mw-float]:not([data-mw-measuring], [data-mw-native]) {
    text-indent: calc(var(--mw-ti, 0) * var(--mw-cw, 1px)) !important;
  }

  /* Boxes that stay in the browser's flow with engine-forced geometry
   * (the shared rule above) and quantized engine margins: a mixed block
   * container's child, placed so the container's anonymous runs of text
   * land on their rows (specs/cell-model.md "Inline content"), and an
   * in-flow multicol SPANNER (specs/multicol.md), which paragraphs
   * fragment around. `relative` makes them the containing block for
   * their absolutized descendants and carries a sticky box's shift and
   * the half-leading lift; the engine owns every inset, so an authored
   * one stays inert. */
  mono-wind [data-mw-flow]:not([data-mw-measuring]),
  mono-wind [data-mw-float]:not([data-mw-measuring]),
  mono-wind [data-mw-multicol-flow-span]:not([data-mw-measuring]) {
    position: relative !important;
    top: calc((var(--mw-sy) + var(--mw-lhs, 0)) * var(--mw-ch, 1px)) !important;
    right: auto !important;
    bottom: auto !important;
    left: calc(var(--mw-sx) * var(--mw-cw, 1px)) !important;
    margin: calc(var(--mw-mt, 0) * var(--mw-ch, 1px)) 0 calc(var(--mw-mb, 0) * var(--mw-ch, 1px))
      calc(var(--mw-ml, 0) * var(--mw-cw, 1px)) !important;
  }
  mono-wind [data-mw-multicol-flow-span]:not([data-mw-measuring]) {
    column-span: all !important;
  }
  /* A formatting-context root among flow children — a container, or a leaf
   * CSS makes one — keeps its own: its first child's top margin stays
   * inside it, where the engine put that child (the shadow slot does the
   * same for the host's, element.ts), and it steps aside from a float
   * rather than passing under (specs/float.md). */
  mono-wind [data-mw-flow="box"]:not([data-mw-measuring]) {
    contain: layout !important;
  }
  /* A float (specs/float.md): the browser floats the engine's box, and
   * the lines of the leaf flow children beside it shorten as the engine
   * shortened them, while a root flow child steps aside as the engine
   * placed it. Both margin boxes take part in the browser's fit — a
   * float's is the exclusion, a root's must not overlap one — so each
   * gives back the shared rule's widening on its right margin: the
   * tracking allowance and, as a quarter pixel, the layout-unit headroom.
   * A root or a later float that fits the engine's band exactly then
   * fits natively too, and a line beside a float keeps its headroom. */
  mono-wind [data-mw-float]:not([data-mw-measuring]),
  mono-wind [data-mw-flow="box"]:not([data-mw-measuring]) {
    margin-right: calc(
      (var(--mw-mr, 0) - max(0, var(--mw-ls, 0) - var(--mw-pr, 0) - var(--mw-br, 0))) *
        var(--mw-cw, 1px) - 0.25px
    ) !important;
  }
  mono-wind [data-mw-float="left"]:not([data-mw-measuring]) {
    float: left !important;
  }
  mono-wind [data-mw-float="right"]:not([data-mw-measuring]) {
    float: right !important;
  }

  /* Spanner-split paragraph flow trusts the NATIVE balancer per segment
   * (probed pixel-exact in all three engines under quantized inputs):
   * `column-fill: balance` and the natural height replace the
   * fill-to-computed-height reconstruction of the base multicol rule. */
  mono-wind [data-mw-multicol][data-mw-multicol-balance]:not([data-mw-measuring]) {
    column-fill: balance !important;
    height: auto !important;
  }

  /* Paragraph gaps under a spanner-split (balance) container: margins
   * derail the native balancers, so the engine-collapsed gaps ride as
   * PADDING — each inter-paragraph gap as the preceding paragraph's
   * padding-bottom (`--mw-mb`), the segment-leading gap as the first
   * paragraph's padding-top (`--mw-mt`); how each engine breaks it:
   * specs/multicol.md "Fragmenting text-leaf children". */
  mono-wind [data-mw-multicol-balance] > [data-mw-multicol-flow]:not([data-mw-measuring]) {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    padding-top: calc(var(--mw-mt, 0) * var(--mw-ch, 1px)) !important;
    padding-bottom: calc(var(--mw-mb, 0) * var(--mw-ch, 1px)) !important;
  }

  /* A box clipping both axes (`overflow: hidden` or `clip`) keeps its
   * content inside the engine-allocated box as `clip` (no scroll
   * container, cheaper, the engine scrolling neither). Gated, as the read
   * tells `hidden` from `clip` (specs/cell-model.md "Overflow"). */
  mono-wind [data-mw-clip]:not([data-mw-measuring]) {
    overflow: clip !important;
  }

  /* Inline (relative/sticky) elements with authored relative insets
   * (specs/positioning.md): the engine writes the cell-rounded offsets into
   * `--mw-it/ir/ib/il` and this rule applies them on the grid. A side
   * the engine leaves unwritten keeps the reset, is invalid at
   * computed-value time and falls back to `auto` — so authored subsets
   * work and the author's raw off-grid values never apply. Gated on the
   * measuring flag so the style reader sees the authored values (no echo
   * loop). Also pins `position: relative`: an inline sticky element's
   * shift rides these vars (specs/sticky.md). */
  mono-wind [data-mw-inline-inset]:not([data-mw-measuring]) {
    position: relative !important;
    top: calc(var(--mw-it) * var(--mw-ch, 1px)) !important;
    right: calc(var(--mw-ir) * var(--mw-cw, 1px)) !important;
    bottom: calc(var(--mw-ib) * var(--mw-ch, 1px)) !important;
    left: calc(var(--mw-il) * var(--mw-cw, 1px)) !important;
  }

  /* === Engine state attributes (warnings, hiding, alignment) =========== */

  /* Misparented table content (no anonymous boxes, specs/table.md) and
   * <col>/<colgroup> boxes: fully hidden, subtree included. */
  mono-wind [data-mw-table-hidden]:not([data-mw-measuring]) {
    display: none !important;
  }

  /* A native region clipped where the grid's later ink covers it
   * (specs/native-regions.md "Paint"): the browser draws its contents
   * over every cell. Before force-hidden, which hides it whole. */
  mono-wind [data-mw-native-clip]:not([data-mw-measuring]) {
    clip-path: var(--mw-clip) !important;
  }

  /* An anchored box `position-visibility` hides (specs/anchor-positioning.md),
   * as the browsers hide it at paint: clipped whole, its subtree with it,
   * so it paints and takes no hit yet keeps the focus and the
   * accessibility tree; a top-layer element inside it stays out of the
   * clip, as in CSS. The clip snaps under the read flags' transition
   * mask (above). */
  mono-wind [data-mw-force-hidden]:not([data-mw-measuring]) {
    clip-path: inset(50%) !important;
  }

  /* A container whose anonymous runs are misparented table content
   * (stray text in a div-table, specs/table.md): the runs have no element
   * to hide, so the container hides its text, and its laid-out children
   * — the rows or cells — show through where they read visible
   * (specs/visibility.md). */
  mono-wind [data-mw-hidden-runs]:not([data-mw-measuring]) {
    visibility: hidden !important;
  }
  mono-wind
    :where([data-mw-hidden-runs] > [data-mw-laid-out]):not(
      [data-mw-measuring],
      [data-mw-invisible]
    ) {
    visibility: visible !important;
  }
}
